import { useAuth, SignInButton, UserButton } from "@clerk/clerk-react";
import { useEffect, useRef, useState } from "react";
import { useLibrary, type LibrarySnapshot } from "../contexts/LibraryContext";
import { writeLocalState } from "../storage";
const accountApi =
  import.meta.env.VITE_CONTENT_API_URL ||
  "https://eb-content-dev.dvg-0fd.workers.dev";
function snapshot(l: ReturnType<typeof useLibrary>): LibrarySnapshot {
  return {
    favorites: l.favorites,
    savedSeries: l.savedSeries,
    history: l.history,
    progress: l.progress,
    completed: l.completed,
    collections: l.collections,
    reading: l.reading,
  };
}
export function AccountSync() {
  return import.meta.env.VITE_CLERK_PUBLISHABLE_KEY ? <ConnectedSync /> : null;
}
function ConnectedSync() {
  const { isLoaded, isSignedIn, getToken, userId } = useAuth();
  const library = useLibrary();
  const state = snapshot(library),
    serialized = JSON.stringify(state);
  const [revision, setRevision] = useState<number | null>(null),
    [message, setMessage] = useState(""),
    [importable, setImportable] = useState<LibrarySnapshot | null>(null);
  const baseline = useRef(""),
    working = useRef(false),
    guest = useRef<LibrarySnapshot | null>(null),
    session = useRef<string | null>(null);
  const call = async (method = "GET", body?: unknown) => {
    const token = await getToken();
    const response = await fetch(`${accountApi}/api/library/me`, {
      method,
      headers: {
        Authorization: `Bearer ${token}`,
        ...(body ? { "Content-Type": "application/json" } : {}),
      },
      body: body ? JSON.stringify(body) : undefined,
    });
    const data = await response.json();
    if (!response.ok) throw Error(data.error || "Synchronization failed.");
    return data;
  };
  const load = async () => {
    const identity = session.current;
    const data = await call();
    if (session.current !== identity) return;
    const next = data.data || {
      favorites: {},
      savedSeries: {},
      history: [],
      progress: {},
      completed: [],
      collections: {},
      reading: {},
    };
    baseline.current = JSON.stringify(next);
    library.replaceSnapshot(next);
    setRevision(data.revision);
    setMessage("Your library is up to date.");
  };
  useEffect(() => {
    if (!isLoaded || !library.hydrated) return;
    let active = true;
    setRevision(null);
    if (isSignedIn && userId) {
      if (!session.current) guest.current = state;
      session.current = userId;
      library.setCloudSession(true);
      setImportable(
        guest.current &&
          (Object.keys(guest.current.favorites).length ||
            guest.current.history.length)
          ? guest.current
          : null,
      );
      library.replaceSnapshot({
        favorites: {},
        savedSeries: {},
        history: [],
        progress: {},
        completed: [],
        collections: {},
        reading: {},
      });
      call()
        .then((d) => {
          if (!active) return;
          const next = d.data || {
            favorites: {},
            savedSeries: {},
            history: [],
            progress: {},
            completed: [],
            collections: {},
            reading: {},
          };
          baseline.current = JSON.stringify(next);
          library.replaceSnapshot(next);
          setRevision(d.revision);
          setMessage("Your library is up to date.");
        })
        .catch((e) => {
          if (active) setMessage(e.message);
        });
    } else if (session.current) {
      session.current = null;
      if (guest.current) library.replaceSnapshot(guest.current);
      library.setCloudSession(false);
      setMessage("");
    }
    return () => {
      active = false;
    };
  }, [isLoaded, isSignedIn, userId, library.hydrated]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!isSignedIn || revision === null || serialized === baseline.current)
      return;
    const timer = setTimeout(async () => {
      if (working.current) return;
      working.current = true;
      const identity = session.current;
      try {
        const result = await call("POST", {
          expectedRevision: revision,
          data: state,
        });
        if (session.current !== identity) return;
        baseline.current = serialized;
        setRevision(result.revision);
        setMessage("Saved across your devices.");
      } catch (e) {
        await writeLocalState(`sync-recovery-${identity}`, state);
        if (session.current !== identity) return;
        setImportable(state);
        setMessage(
          e instanceof Error
            ? e.message
            : "Sync failed. Your changes are saved on this device.",
        );
        setRevision(null);
      } finally {
        working.current = false;
      }
    }, 1500);
    return () => clearTimeout(timer);
  }, [serialized, revision, isSignedIn]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const focus = () => {
      if (
        isSignedIn &&
        revision !== null &&
        !working.current &&
        baseline.current === serialized
      )
        load().catch((e) => setMessage(e.message));
    };
    window.addEventListener("focus", focus);
    return () => window.removeEventListener("focus", focus);
  }, [serialized, isSignedIn, revision]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!isLoaded) return null;
  return (
    <div className="account-strip">
      {isSignedIn ? (
        <>
          <UserButton />
          <span role="status">{message}</span>
          {importable ? (
            <button
              onClick={() => {
                library.replaceSnapshot({
                  ...state,
                  favorites: { ...importable.favorites, ...state.favorites },
                  savedSeries: {
                    ...importable.savedSeries,
                    ...state.savedSeries,
                  },
                  collections: {
                    ...importable.collections,
                    ...state.collections,
                  },
                  reading: { ...importable.reading, ...state.reading },
                  progress: { ...importable.progress, ...state.progress },
                  history: [
                    ...state.history,
                    ...importable.history.filter(
                      (w) => !state.history.some((x) => x.id === w.id),
                    ),
                  ].slice(0, 100),
                  completed: [
                    ...new Set([...state.completed, ...importable.completed]),
                  ],
                });
                setImportable(null);
              }}
            >
              Import this device’s saved library
            </button>
          ) : null}
          {revision === null ? (
            <button onClick={() => load().catch((e) => setMessage(e.message))}>
              Load cloud version
            </button>
          ) : null}
          <button
            onClick={() => {
              const blob = new Blob([serialized], { type: "application/json" }),
                url = URL.createObjectURL(blob),
                a = document.createElement("a");
              a.href = url;
              a.download = "my-eternally-blessed-library.json";
              a.click();
              URL.revokeObjectURL(url);
            }}
          >
            Export saves
          </button>
          <button
            onClick={async () => {
              if (
                window.confirm(
                  "Delete your synchronized library? Local saves will remain on this device.",
                )
              ) {
                try {
                  await call("DELETE");
                } catch (e) {
                  setMessage(
                    e instanceof Error
                      ? e.message
                      : "Could not delete cloud saves.",
                  );
                  return;
                }
                baseline.current = serialized;
                setRevision(null);
                setMessage(
                  "Synchronized data deleted. Sign out to continue using local saves.",
                );
              }
            }}
          >
            Delete cloud saves
          </button>
        </>
      ) : (
        <>
          <span>Keep your place, wherever you are.</span>
          <SignInButton mode="modal">
            <button>Sign in to sync</button>
          </SignInButton>
        </>
      )}
    </div>
  );
}
